import React from 'react';
import { useQuery } from 'react-query';
import { invoicesApi } from '../../services/api/invoices';
import { FileText, DollarSign, CheckCircle, Clock } from 'lucide-react';

interface InvoiceSummaryCardProps {
  projectId: string;
}

const InvoiceSummaryCard: React.FC<InvoiceSummaryCardProps> = ({ projectId }) => {
  const { data: summary, isLoading } = useQuery({
    queryKey: ['invoices', 'summary', projectId],
    queryFn: () => invoicesApi.getProjectSummary(projectId),
  });

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Facturas Emitidas
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Invoiced */}
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="bg-blue-100 rounded-lg p-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-xs font-medium text-blue-600">
              {summary.invoiceCount} facturas
            </span>
          </div>
          <p className="text-sm font-medium text-blue-900 mb-1">
            Total Facturado
          </p>
          <p className="text-xl font-bold text-blue-900">
            {formatCurrency(summary.totalInvoiced)}
          </p>
        </div>

        {/* Total Paid */}
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="bg-green-100 rounded-lg p-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-xs font-medium text-green-600">
              {summary.paidCount} pagadas
            </span>
          </div>
          <p className="text-sm font-medium text-green-900 mb-1">
            Total Pagado
          </p>
          <p className="text-xl font-bold text-green-900">
            {formatCurrency(summary.totalPaid)}
          </p>
        </div>

        {/* Total Pending */}
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="bg-yellow-100 rounded-lg p-2">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <span className="text-xs font-medium text-yellow-600">
              {summary.pendingCount} pendientes
            </span>
          </div>
          <p className="text-sm font-medium text-yellow-900 mb-1">
            Total Pendiente
          </p>
          <p className="text-xl font-bold text-yellow-900">
            {formatCurrency(summary.totalPending)}
          </p>
        </div>

        {/* Total Overdue */}
        <div className="bg-red-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="bg-red-100 rounded-lg p-2">
              <FileText className="w-5 h-5 text-red-600" />
            </div>
            <span className="text-xs font-medium text-red-600">
              {summary.overdueCount} vencidas
            </span>
          </div>
          <p className="text-sm font-medium text-red-900 mb-1">
            Total Vencido
          </p>
          <p className="text-xl font-bold text-red-900">
            {formatCurrency(summary.totalOverdue)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvoiceSummaryCard;
